import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal";
import { analytics, EVENTS, EVENT_FN } from "../analytics";
import { useInterestRegistration } from "../hooks/useInterestRegistration";
import { diagnosisPrice } from "../constant";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  service: { id: string; name: string } | null;
  onClose: () => void;
}

const ServiceUnavailableModal: React.FC<Props> = ({ service, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const { status, register, reset } = useInterestRegistration();

  const handleClose = () => {
    setEmail("");
    reset();
    onClose();
  };

  const handleNotifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!service || !EMAIL_RE.test(email) || status === "submitting") return;

    const result = await register(email, {
      source_page: "service_unavailable",
      interested_service: service.id,
    });
    if (result !== "error") {
      analytics.track(EVENT_FN.serviceUnavailableNotify(service.id));
    }
  };

  const goToDiagnosis = () => {
    analytics.track(EVENTS.CLICK_SERVICE_UNAVAILABLE_TO_DIAGNOSE, {
      from_service: service?.id,
    });
    handleClose();
    navigate("/check-service");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isDone = status === "success" || status === "duplicate";

  return (
    <Modal open={!!service} onClose={handleClose}>
      <h3 className="text-xl font-bold text-main mb-3">
        {t("serviceUnavailableModal.title")}
      </h3>
      <p className="text-gray-600 text-sm leading-relaxed mb-6 whitespace-pre-line">
        {t("serviceUnavailableModal.body", { serviceName: service?.name ?? "" })}
      </p>

      {isDone ? (
        <p className="text-sm text-main font-semibold mb-6 bg-sub rounded-lg px-4 py-3">
          {status === "duplicate"
            ? t("serviceUnavailableModal.alreadyRegistered")
            : t("serviceUnavailableModal.notifySuccess")}
        </p>
      ) : (
        <form onSubmit={handleNotifySubmit} className="mb-6">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("serviceUnavailableModal.placeholder") as string}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-main"
          />
          {status === "error" && (
            <p className="text-red-500 text-xs mb-3">{t("serviceUnavailableModal.error")}</p>
          )}
          <button
            type="submit"
            disabled={status === "submitting"}
            className="w-full bg-main text-white font-bold px-6 py-4 rounded-xl hover:opacity-90 transition-colors disabled:opacity-60"
          >
            {status === "submitting"
              ? t("serviceUnavailableModal.notifySubmitting")
              : t("serviceUnavailableModal.notifyCta")}
          </button>
        </form>
      )}

      <div className="flex items-center gap-3 mb-6">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs text-gray-400">{t("serviceUnavailableModal.or")}</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <p className="text-sm text-gray-600 mb-3">
        {t("serviceUnavailableModal.diagnosisPrompt").replace("{Check_Price}", diagnosisPrice.toString())}
      </p>
      <button
        onClick={goToDiagnosis}
        className="w-full bg-accent text-main font-bold px-6 py-4 rounded-xl hover:bg-yellow-400 transition-colors"
      >
        {t("serviceUnavailableModal.cta")}
      </button>
    </Modal>
  );
};

export default ServiceUnavailableModal;
